import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { 
    Award, Trash2, Edit2, Eye, EyeOff, Save, Loader2 
} from 'lucide-react';
import './CertificateManager.css';

const CertificateManager = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Form states
    const [title, setTitle] = useState('');
    const [minPercentage, setMinPercentage] = useState(80);
    const [titleText, setTitleText] = useState('СЕРТИФІКАТ');
    const [subText, setSubText] = useState('про успішне проходження навчання');
    const [signatureText, setSignatureText] = useState('Тренер');
    const [signatureName, setSignatureName] = useState('Адміністрація');
    const [backgroundType, setBackgroundType] = useState('classic');
    const [hasQrCode, setHasQrCode] = useState(true);
    const [targetTestType, setTargetTestType] = useState('any');
    const [targetTestId, setTargetTestId] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [city, setCity] = useState('');

    // Metadata option lists
    const [cities, setCities] = useState([]);
    const [targetTestOptions, setTargetTestOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);

    const { success, error } = useToast();
    const confirm = useConfirm();

    // Fetch template list
    const fetchTemplates = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/certificates/templates`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTemplates(res.data);
        } catch (err) {
            error(err.response?.data?.error || 'Помилка при завантаженні шаблонів сертифікатів');
        } finally {
            setLoading(false);
        }
    }, [error]);

    useEffect(() => {
        fetchTemplates();
        
        // Fetch cities
        const fetchCities = async () => {
            try {
                const res = await axios.get(`${API_URL}/cities`);
                setCities(res.data);
            } catch (err) {
                console.error('Failed to load cities:', err);
            }
        };
        fetchCities();
    }, [fetchTemplates]);

    // Load test options dynamically when targetTestType changes
    useEffect(() => {
        const fetchTestOptions = async () => {
            if (targetTestType === 'any') {
                setTargetTestOptions([]);
                setTargetTestId('');
                return;
            }

            setLoadingOptions(true);
            try {
                const token = localStorage.getItem('token');
                const config = { headers: { Authorization: `Bearer ${token}` } };
                let url = '';

                if (targetTestType === 'Quiz') url = `${API_URL}/quiz`;
                else if (targetTestType === 'DeskTemplate') url = `${API_URL}/templates`;
                else if (targetTestType === 'MultiDeskTest') url = `${API_URL}/tests/multi`;
                else if (targetTestType === 'GameScenario') url = `${API_URL}/game-scenarios`;
                else if (targetTestType === 'ComplexTest') url = `${API_URL}/complex-tests`;
                else if (targetTestType === 'PizzaTemplate') url = `${API_URL}/pizza-templates/templates`;

                if (url) {
                    const res = await axios.get(url, config);
                    setTargetTestOptions(res.data);
                } else {
                    setTargetTestOptions([]);
                }
            } catch (err) {
                console.error('Failed to fetch target test options:', err);
                error('Не вдалося завантажити список доступних тестів');
            } finally {
                setLoadingOptions(false);
            }
        };

        fetchTestOptions();
    }, [targetTestType, error]);

    const resetForm = () => {
        setEditingId(null);
        setTitle('');
        setMinPercentage(80);
        setTitleText('СЕРТИФІКАТ');
        setSubText('про успішне проходження навчання');
        setSignatureText('Тренер');
        setSignatureName('Адміністрація');
        setBackgroundType('classic');
        setHasQrCode(true);
        setTargetTestType('any');
        setTargetTestId('');
        setIsActive(true);
        setCity('');
    };

    const handleSaveTemplate = async (e) => {
        e.preventDefault();
        if (!title.trim()) {
            error('Введіть назву шаблону');
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const payload = {
                title,
                minPercentage: Number(minPercentage),
                titleText,
                subText,
                signatureText,
                signatureName,
                backgroundType,
                hasQrCode,
                targetTestType,
                targetTestId: targetTestId || null,
                isActive,
                city
            };

            if (editingId) {
                const res = await axios.put(`${API_URL}/certificates/templates/${editingId}`, payload, config);
                success('Шаблон сертифіката успішно оновлено!');
                setTemplates(prev => prev.map(t => t._id === editingId ? res.data : t));
            } else {
                const res = await axios.post(`${API_URL}/certificates/templates`, payload, config);
                success('Шаблон сертифіката створено!');
                setTemplates(prev => [res.data, ...prev]);
            }
            resetForm();
        } catch (err) {
            error(err.response?.data?.error || 'Помилка при збереженні шаблону');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (tmpl) => {
        setEditingId(tmpl._id);
        setTitle(tmpl.title || '');
        setMinPercentage(tmpl.minPercentage || 80);
        setTitleText(tmpl.titleText || 'СЕРТИФІКАТ');
        setSubText(tmpl.subText || 'про успішне проходження навчання');
        setSignatureText(tmpl.signatureText || 'Тренер');
        setSignatureName(tmpl.signatureName || 'Адміністрація');
        setBackgroundType(tmpl.backgroundType || 'classic');
        setHasQrCode(tmpl.hasQrCode !== false);
        setTargetTestType(tmpl.targetTestType || 'any');
        
        // Timeout to allow the target test type loading effect to trigger first
        setTimeout(() => {
            setTargetTestId(tmpl.targetTestId || '');
        }, 300);

        setIsActive(tmpl.isActive !== false);
        setCity(tmpl.city || '');
    };

    const handleDelete = async (id) => {
        const isConfirmed = await confirm({
            title: 'Видалення шаблону сертифіката',
            message: 'Ви впевнені, що хочете видалити цей шаблон сертифіката?',
            confirmText: 'Видалити',
            cancelText: 'Скасувати'
        });
        if (!isConfirmed) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/certificates/templates/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            success('Шаблон видалено');
            setTemplates(prev => prev.filter(t => t._id !== id));
            if (editingId === id) resetForm();
        } catch (err) {
            error(err.response?.data?.error || 'Помилка видалення');
        }
    };

    const getTestTypeLabel = (type) => {
        const types = {
            'any': 'Будь-який тест',
            'Quiz': '📝 Квіз',
            'DeskTemplate': '🍽️ Стіл сервірування',
            'MultiDeskTest': '🥞 Комплексне сервірування',
            'GameScenario': '🎮 Ігровий сценарій',
            'ComplexTest': '⚡ Комплексний тест',
            'PizzaTemplate': '🍕 Конструктор піци'
        };
        return types[type] || type;
    };

    return (
        <div className="cm-container">
            <header className="cm-header">
                <div className="cm-title-area">
                    <Award size={32} className="cm-header-icon" />
                    <div>
                        <h1>Конструктор сертифікатів</h1>
                        <p>Налаштуйте автоматичну видачу сертифікатів для успішних студентів</p>
                    </div>
                </div>
            </header>

            <div className="cm-workspace">
                {/* Form & Editor */}
                <div className="cm-editor-section">
                    <form onSubmit={handleSaveTemplate} className="cm-form-card">
                        <div className="cm-form-header">
                            <h3>{editingId ? 'Редагувати шаблон' : 'Створити новий шаблон'}</h3>
                            {editingId && (
                                <button type="button" className="cm-btn-cancel" onClick={resetForm}>
                                    Скасувати
                                </button>
                            )}
                        </div>

                        <div className="cm-form-grid">
                            <div className="cm-form-group span-2">
                                <label>Назва шаблону (для адміна)</label>
                                <input 
                                    type="text" 
                                    value={title} 
                                    onChange={(e) => setTitle(e.target.value)} 
                                    placeholder="Наприклад: Сертифікат за квіз по стандартах" 
                                    required 
                                />
                            </div>

                            <div className="cm-form-group">
                                <label>Прохідний поріг (%)</label>
                                <input 
                                    type="number" 
                                    min="0" 
                                    max="100" 
                                    value={minPercentage} 
                                    onChange={(e) => setMinPercentage(e.target.value)} 
                                    required 
                                />
                            </div>

                            <div className="cm-form-group">
                                <label>Місто / Локація</label>
                                <select value={city} onChange={(e) => setCity(e.target.value)}>
                                    <option value="">Для всіх міст</option>
                                    {cities.map(c => (
                                        <option key={c._id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="cm-form-group">
                                <label>Тип тесту</label>
                                <select value={targetTestType} onChange={(e) => setTargetTestType(e.target.value)}>
                                    <option value="any">Будь-який</option>
                                    <option value="Quiz">📝 Квіз</option>
                                    <option value="DeskTemplate">🍽️ Стіл сервірування</option>
                                    <option value="MultiDeskTest">🥞 Комплексне сервірування</option>
                                    <option value="GameScenario">🎮 Ігровий сценарій</option>
                                    <option value="ComplexTest">⚡ Комплексний тест</option>
                                    <option value="PizzaTemplate">🍕 Конструктор піци</option>
                                </select>
                            </div>

                            <div className="cm-form-group">
                                <label>Конкретний тест / сценарій</label>
                                <select 
                                    value={targetTestId} 
                                    onChange={(e) => setTargetTestId(e.target.value)}
                                    disabled={targetTestType === 'any' || loadingOptions}
                                >
                                    <option value="">Всі тести цього типу</option>
                                    {targetTestOptions.map(opt => (
                                        <option key={opt._id} value={opt._id}>
                                            {opt.title || opt.name || opt.templateName || 'Без назви'}
                                        </option>
                                    ))}
                                </select>
                                {loadingOptions && <div className="cm-select-loader"><Loader2 size={12} className="spin" /> Завантаження...</div>}
                            </div>

                            <div className="cm-form-group span-2">
                                <label>Заголовок у сертифікаті</label>
                                <input 
                                    type="text" 
                                    value={titleText} 
                                    onChange={(e) => setTitleText(e.target.value)} 
                                    placeholder="СЕРТИФІКАТ" 
                                />
                            </div>

                            <div className="cm-form-group span-2">
                                <label>Опис / Додатковий текст</label>
                                <textarea 
                                    value={subText} 
                                    onChange={(e) => setSubText(e.target.value)} 
                                    rows="2"
                                    placeholder="про успішне проходження навчання за темою стандарти гостинності"
                                />
                            </div>

                            <div className="cm-form-group">
                                <label>Посада підписувача</label>
                                <input 
                                    type="text" 
                                    value={signatureText} 
                                    onChange={(e) => setSignatureText(e.target.value)} 
                                    placeholder="Тренер" 
                                />
                            </div>

                            <div className="cm-form-group">
                                <label>ПІБ підписувача</label>
                                <input 
                                    type="text" 
                                    value={signatureName} 
                                    onChange={(e) => setSignatureName(e.target.value)} 
                                    placeholder="Адміністрація" 
                                />
                            </div>

                            <div className="cm-form-group">
                                <label>Стиль оформлення</label>
                                <select value={backgroundType} onChange={(e) => setBackgroundType(e.target.value)}>
                                    <option value="classic">Класичний (Кремовий / Золотий)</option>
                                    <option value="modern">Сучасний (Темний / Градієнт)</option>
                                    <option value="minimal">Мінімалістичний (Чистий білий)</option>
                                </select>
                            </div>

                            <div className="cm-form-checkboxes">
                                <label className="cm-checkbox-label">
                                    <input 
                                        type="checkbox" 
                                        checked={hasQrCode} 
                                        onChange={(e) => setHasQrCode(e.target.checked)} 
                                    />
                                    <span>Включити QR-код перевірки</span>
                                </label>
                                <label className="cm-checkbox-label">
                                    <input 
                                        type="checkbox" 
                                        checked={isActive} 
                                        onChange={(e) => setIsActive(e.target.checked)} 
                                    />
                                    <span>Шаблон активний</span>
                                </label>
                            </div>
                        </div>

                        <button type="submit" className="cm-btn-submit" disabled={saving}>
                            {saving ? <Loader2 size={18} className="spin" /> : <Save size={18} />}
                            <span>{editingId ? 'Зберегти зміни' : 'Створити шаблон'}</span>
                        </button>
                    </form>

                    {/* Live Preview Card */}
                    <div className="cm-preview-wrapper">
                        <h4>Попередній перегляд сертифіката (на екрані)</h4>
                        <div className={`cm-preview-frame ${backgroundType}`}>
                            {/* Corner decorations for classic */}
                            {(backgroundType === 'classic') && (
                                <>
                                    <div className="p-corner top-left"></div>
                                    <div className="p-corner top-right"></div>
                                    <div className="p-corner bottom-left"></div>
                                    <div className="p-corner bottom-right"></div>
                                </>
                            )}
                            <div className="cm-preview-content">
                                <img src="/km-logo.png" alt="Країна Мрій" className="p-logo-img" />
                                <h2 className="p-title">{titleText || 'СЕРТИФІКАТ'}</h2>
                                <p className="p-subtitle">{subText || 'про успішне проходження навчання'}</p>
                                <h1 className="p-student-name">Ім'я Прізвище Студента</h1>
                                
                                <div className="p-divider-container">
                                    <div className="p-divider-line"></div>
                                    <div className="p-divider-diamond"></div>
                                    <div className="p-divider-line"></div>
                                </div>
                                
                                <p className="p-info-text">про успішне проходження курсу:</p>
                                <h3 className="p-test-title">
                                    {targetTestType === 'any' ? 'Назва курсу або тесту' : 'Конкретний вибраний тест'}
                                </h3>
                                
                                <div className="p-footer">
                                    <div className="p-footer-col left">
                                        {hasQrCode && (
                                            <div className="p-qr-placeholder" style={{ marginBottom: '0.4rem' }}>
                                                <span>QR CODE</span>
                                            </div>
                                        )}
                                        <span className="p-label">Дата видачі</span>
                                        <span className="p-val">{new Date().toLocaleDateString('uk-UA')}</span>
                                    </div>
                                    <div className="p-footer-col center">
                                        {(backgroundType === 'classic') && (
                                            <div className="cert-trophy-badge">
                                                <img src="/trophy.svg" alt="Кубок Переможця" className="cert-trophy-img" style={{ height: '60px' }} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-footer-col right">
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                            <span className="p-sig-handwritten">{signatureName || 'Адміністрація'}</span>
                                            <div className="p-sig-line" style={{ width: '100%', opacity: 0.6 }}></div>
                                            <span className="p-label">{signatureText || 'Посада'}</span>
                                            <span className="p-val">{signatureName || 'ПІБ'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Templates List */}
                <div className="cm-list-section">
                    <div className="cm-section-title-row">
                        <h3>Список шаблонів ({templates.length})</h3>
                    </div>

                    {loading ? (
                        <div className="cm-list-loading">
                            <Loader2 size={32} className="spin" />
                            <p>Завантаження шаблонів...</p>
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="cm-empty-list">
                            <Award size={48} className="empty-icon" />
                            <p>Шаблонів сертифікатів ще не створено.</p>
                        </div>
                    ) : (
                        <div className="cm-templates-list">
                            {templates.map(tmpl => (
                                <div key={tmpl._id} className={`cm-tmpl-card ${tmpl.isActive ? 'active' : 'inactive'}`}>
                                    <div className="cm-tmpl-info">
                                        <div className="cm-tmpl-title-row">
                                            <h4>{tmpl.title}</h4>
                                            <span className={`cm-status-badge ${tmpl.isActive ? 'active' : 'inactive'}`}>
                                                {tmpl.isActive ? <Eye size={12} /> : <EyeOff size={12} />}
                                                {tmpl.isActive ? 'Активний' : 'Неактивний'}
                                            </span>
                                        </div>
                                        <p className="cm-tmpl-desc">
                                            Тип: <strong>{getTestTypeLabel(tmpl.targetTestType)}</strong> 
                                            {tmpl.city && <> | Місто: <strong>{tmpl.city}</strong></>} 
                                            | Поріг: <strong>{tmpl.minPercentage}%</strong>
                                        </p>
                                        <p className="cm-tmpl-style-meta">
                                            Стиль: <span>{tmpl.backgroundType}</span> 
                                            | QR-код: <span>{tmpl.hasQrCode ? 'Так' : 'Ні'}</span>
                                        </p>
                                    </div>

                                    <div className="cm-tmpl-actions">
                                        <button 
                                            type="button" 
                                            className="cm-btn-edit" 
                                            onClick={() => handleEdit(tmpl)}
                                            title="Редагувати"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button 
                                            type="button" 
                                            className="cm-btn-delete" 
                                            onClick={() => handleDelete(tmpl._id)}
                                            title="Видалити"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CertificateManager;
